import { useCallback, useEffect, useMemo, useState } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { Button, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { useTopicsDataContext } from "app/pages/activation/context/Topics/TopicsDataContext";
import { useTopicsModalContext } from "app/pages/activation/context/Topics/TopicsModalContext";
import { getUniqKwNames } from "app/pages/activation/helpers/home/keywords";
import { activationTracking } from "app/pages/activation/services/tracking";

import { normalize, MAX_TOPIC_NAME_LENGTH, allowedRegex } from "./constants";
import { collisionKeys } from "./dataAutomation";
import Item from "./Item/Item";
import * as SC from "./styles";
import { ALLOWED_CHARS, DUPLICATE, MAX_LENGTH_TEXT, NAME_USED, SKIP, SUBMIT, SUBTITLE, TITLE, TOOLTIP } from "./texts";

export const Collision = () => {
    const { modalData, onClose } = useTopicsModalContext();
    const { keywordsGroups } = useTopicsDataContext();
    const itemsCount = modalData.data?.collisionList?.length ?? 0;
    const track = activationTracking.home;

    useEffect(() => {
        track.trackTopicsModalCollision(itemsCount);
    }, [track, itemsCount]);

    const existingNamesSet = useMemo(() => getUniqKwNames(keywordsGroups), [keywordsGroups]);

    const [values, setValues] = useState<string[]>(Array.from({ length: itemsCount }, () => ""));
    const [errors, setErrors] = useState<string[]>(Array.from({ length: itemsCount }, () => ""));
    const [valids, setValids] = useState<boolean[]>(Array.from({ length: itemsCount }, () => false));

    useEffect(() => {
        setValues(Array.from({ length: itemsCount }, () => ""));
        setErrors(Array.from({ length: itemsCount }, () => ""));
        setValids(Array.from({ length: itemsCount }, () => false));
    }, [itemsCount]);

    const validateAll = useCallback(
        (vals: string[]) => {
            const nextErrors = new Array<string>(vals.length).fill("");
            const nextValids = new Array<boolean>(vals.length).fill(false);
            const normalizedCounts = new Map<string, number>();

            vals.forEach((v) => {
                const n = normalize(v || "");
                if (!n) {
                    return;
                }
                normalizedCounts.set(n, (normalizedCounts.get(n) || 0) + 1);
            });

            vals.forEach((v, i) => {
                const val = v || "";

                if (val.length > MAX_TOPIC_NAME_LENGTH) {
                    nextErrors[i] = translate(MAX_LENGTH_TEXT, {
                        max: MAX_TOPIC_NAME_LENGTH,
                    });
                } else if (!allowedRegex.test(val)) {
                    nextErrors[i] = translate(ALLOWED_CHARS);
                } else {
                    const n = normalize(val);
                    if (n && existingNamesSet.has(n)) {
                        nextErrors[i] = translate(NAME_USED);
                    } else if (n && (normalizedCounts.get(n) || 0) > 1) {
                        nextErrors[i] = translate(DUPLICATE);
                    } else {
                        nextErrors[i] = "";
                    }
                }
                nextValids[i] = nextErrors[i] === "" && val.trim().length > 0;
            });
            setErrors(nextErrors);
            setValids(nextValids);
        },
        [existingNamesSet],
    );

    const onDebouncedChange = useCallback(
        (index: number, value: string) => {
            setValues((prev) => {
                const next = [...prev];
                next[index] = value;
                validateAll(next);
                return next;
            });
        },
        [validateAll],
    );

    const isAllValid = useMemo(
        () => itemsCount > 0 && valids.length === itemsCount && valids.every(Boolean),
        [itemsCount, valids],
    );

    const handleSubmit = async () => {
        if (modalData.data.onSubmit) {
            track.trackTopicsModalCollisionSubmit();

            onClose();
            await modalData.data.onSubmit(values);
        }
    };

    const handleSkip = () => {
        track.trackTopicsModalCollisionSkip(itemsCount);
        onClose();
    };

    return (
        <SC.Container data-automation={collisionKeys.container}>
            <SC.Content>
                <SC.Title data-automation={collisionKeys.title}>
                    <div>{translate(TITLE, { count: `${itemsCount}/${modalData.data?.topicsNumber}` })}</div>
                    <Tooltip
                        enabled
                        content={translate(TOOLTIP)}
                    >
                        <SWReactIcons
                            className="icon"
                            size="xs"
                            iconName="info"
                        />
                    </Tooltip>
                </SC.Title>
                <SC.SubTitle data-automation={collisionKeys.subtitle}>{translate(SUBTITLE)}</SC.SubTitle>
                <SC.Body data-automation={collisionKeys.body}>
                    {modalData.data?.collisionList?.map((collisionName, idx) => (
                        <Item
                            text={collisionName}
                            key={idx}
                            index={idx}
                            onDebouncedChange={onDebouncedChange}
                            isError={!!errors[idx]}
                            errorMessage={errors[idx]}
                        />
                    ))}
                </SC.Body>
            </SC.Content>

            <SC.Footer data-automation={collisionKeys.footer}>
                <Button
                    onClick={handleSkip}
                    type="flat"
                >
                    {translate(SKIP)}
                </Button>
                <Button
                    onClick={handleSubmit}
                    type="primary"
                    isDisabled={!isAllValid}
                >
                    {translate(SUBMIT)}
                </Button>
            </SC.Footer>
        </SC.Container>
    );
};
